import React, { useState, useEffect } from 'react';
import { authAPI, vendorProfileAPI, locationAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Edit3, X, ChevronDown } from 'lucide-react';
import { UserProfileSkeleton } from '../../components/skeletons';

/**
 * Vendor account page. Once KYC is verified the vendor can't change
 * their profile or KYC, but they CAN still update their mobile number
 * (for OTP) and password — those are handled here via /api/auth/* which
 * is not affected by the KYC lock.
 */

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 },
  sec: { fontSize: 14, fontWeight: 700, color: 'var(--char)', marginBottom: 12, borderBottom: '1px dashed var(--border)', paddingBottom: 8 },
  row: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 12 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box', background: '#fff', cursor: 'pointer', height: 38 },
  btn: (variant = 'primary', disabled = false) => ({
    background: variant === 'primary' ? 'var(--green)' : 'transparent',
    color: variant === 'primary' ? '#fff' : 'var(--green)',
    border: variant === 'primary' ? 'none' : '1.5px solid var(--green)',
    padding: '10px 20px', borderRadius: 9, fontSize: 13, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  }),
  msg: (kind) => ({
    background: kind === 'ok' ? '#E8F5EE' : '#FEE2E2',
    color: kind === 'ok' ? '#1B5C3A' : '#B91C1C',
    padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, fontWeight: 600,
  }),
};

export default function VendorAccount() {
  const { user, setUser, logout } = useAuth();
  const [mobile, setMobile] = useState(user?.mobileNumber || '');
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingPwd, setIsEditingPwd] = useState(false);

  // VendorProfile specific states
  const [profile, setProfile] = useState(null);
  const [firmName, setFirmName] = useState('');
  const [gstin, setGstin] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [pincode, setPincode] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [kycLocked, setKycLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  // States list cascade
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);

  useEffect(() => {
    Promise.all([
      vendorProfileAPI.me().then(res => {
        const p = res.data.data;
        if (p) {
          setProfile(p);
          setFirmName(p.firmName || '');
          setGstin(p.gstin || '');
          setAddress1(p.addressLine1 || '');
          setAddress2(p.addressLine2 || '');
          setPincode(p.pincode || '');
          setState(p.state || '');
          setCity(p.city || '');
          setKycLocked(p.kyc?.status === 'verified' || p.kyc?.status === 'submitted');
        }
      }).catch(() => { }),
      locationAPI.states().then(res => setAllStates(res.data.data || [])).catch(() => { })
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!state) {
      setAllCities([]);
      return;
    }
    locationAPI.cities(state)
      .then(res => setAllCities(res.data.data || []))
      .catch(() => setAllCities([]));
  }, [state]);

  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  const saveProfile = async () => {
    setProfileMsg(null);

    // 1. Validate Personal Information
    const trimmedName = fullName ? fullName.trim() : '';
    if (!trimmedName) {
      setProfileMsg({ kind: 'err', text: 'Full Name cannot be empty.' });
      return;
    }
    if (trimmedName.length < 3 || trimmedName.length > 50) {
      setProfileMsg({ kind: 'err', text: 'Full Name must be between 3 and 50 characters.' });
      return;
    }
    const nameRegex = /^[a-zA-Z\s.-]+$/;
    if (!nameRegex.test(trimmedName)) {
      setProfileMsg({ kind: 'err', text: 'Full Name can only contain letters, spaces, dots, or hyphens.' });
      return;
    }

    let cleanedMobile = mobile ? mobile.replace(/\D/g, '') : '';
    if (cleanedMobile.length === 12 && cleanedMobile.startsWith('91')) {
      cleanedMobile = cleanedMobile.slice(2);
    } else if (cleanedMobile.length === 11 && cleanedMobile.startsWith('0')) {
      cleanedMobile = cleanedMobile.slice(1);
    }
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(cleanedMobile)) {
      setProfileMsg({ kind: 'err', text: 'Please enter a valid 10-digit Indian mobile number.' });
      return;
    }

    // 2. Validate Business Information (if not KYC Locked)
    const trimmedFirmName = firmName ? firmName.trim() : '';
    let trimmedGst = gstin ? gstin.trim().toUpperCase() : '';
    let cleanedPin = pincode ? pincode.replace(/\D/g, '') : '';

    if (!kycLocked) {
      if (!trimmedFirmName) {
        setProfileMsg({ kind: 'err', text: 'Firm name is required.' });
        return;
      }
      if (trimmedFirmName.length < 3 || trimmedFirmName.length > 100) {
        setProfileMsg({ kind: 'err', text: 'Firm name must be between 3 and 100 characters.' });
        return;
      }
      if (!state) {
        setProfileMsg({ kind: 'err', text: 'Please select a state.' });
        return;
      }
      if (!city) {
        setProfileMsg({ kind: 'err', text: 'Please select a city.' });
        return;
      }
      if (trimmedGst) {
        const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstinRegex.test(trimmedGst)) {
          setProfileMsg({ kind: 'err', text: 'Please enter a valid 15-character GSTIN.' });
          return;
        }
      }
      if (cleanedPin) {
        const pinRegex = /^[1-9]\d{5}$/;
        if (!pinRegex.test(cleanedPin)) {
          setProfileMsg({ kind: 'err', text: 'Please enter a valid 6-digit Pincode.' });
          return;
        }
      }
    }

    setSavingProfile(true);
    try {
      // 1. Update personal info (User model)
      const userRes = await authAPI.updateProfile({ fullName: trimmedName, mobileNumber: cleanedMobile });
      if (setUser) setUser(userRes.data.data);

      // 2. Update business details (VendorProfile model) if not KYC locked
      if (!kycLocked) {
        const profileRes = await vendorProfileAPI.update({
          firmName: trimmedFirmName,
          gstin: trimmedGst || undefined,
          addressLine1: address1,
          addressLine2: address2,
          pincode: cleanedPin || undefined,
          state,
          city
        });
        const updatedProfile = profileRes.data.data;
        if (updatedProfile) {
          setProfile(updatedProfile);
          setFirmName(updatedProfile.firmName || '');
          setGstin(updatedProfile.gstin || '');
          setAddress1(updatedProfile.addressLine1 || '');
          setAddress2(updatedProfile.addressLine2 || '');
          setPincode(updatedProfile.pincode || '');
          setState(updatedProfile.state || '');
          setCity(updatedProfile.city || '');
        }
      }

      setProfileMsg({ kind: 'ok', text: 'Profile and business details updated successfully.' });
      setIsEditingProfile(false);
    } catch (e) {
      setProfileMsg({ kind: 'err', text: e.message || e.response?.data?.message || 'Save failed.' });
    } finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    setPwdMsg(null);
    if (pwd.newPassword !== pwd.confirm) {
      return setPwdMsg({ kind: 'err', text: 'New password and confirmation do not match.' });
    }
    if (pwd.newPassword.length < 6) {
      return setPwdMsg({ kind: 'err', text: 'New password must be at least 6 characters.' });
    }
    setSavingPwd(true);
    try {
      await authAPI.changePassword({ currentPassword: pwd.currentPassword, newPassword: pwd.newPassword });
      setPwdMsg({ kind: 'ok', text: 'Password updated.' });
      setPwd({ currentPassword: '', newPassword: '', confirm: '' });
      setIsEditingPwd(false);
    } catch (e) {
      setPwdMsg({ kind: 'err', text: e.response?.data?.message || 'Update failed.' });
    } finally { setSavingPwd(false); }
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
    setDeleteSuccess(false);
  };

  const confirmDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      setDeleteSuccess(true);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete account.");
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = isEditingProfile ? S.input : { ...S.input, background: '#f9f9f9', color: '#666', cursor: 'default' };
  const bizInputStyle = (isEditingProfile && !kycLocked) ? S.input : { ...S.input, background: '#f9f9f9', color: '#666', cursor: 'default' };
  const selectStyle = (isEditingProfile && !kycLocked) ? S.select : { ...S.select, background: '#f9f9f9', color: '#666', cursor: 'default' };
  const pwdInputStyle = isEditingPwd ? S.input : { ...S.input, background: '#f9f9f9', color: '#666', cursor: 'default' };

  if (loading) {
    return <UserProfileSkeleton />;
  }

  return (
    <div style={S.page}>
      <div style={S.title}>My account</div>
      <div style={S.sub}>
        Profile and KYC details are locked once your KYC is verified — but your mobile number and
        password can always be updated here.
      </div>

      {/* Profile */}
      <div style={S.card}>
        <div style={{ ...S.sec, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Profile Details</span>
          {!isEditingProfile ? (
            <button
              onClick={() => setIsEditingProfile(true)}
              style={{
                background: 'none', border: 'none', color: 'var(--green)',
                cursor: 'pointer', fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 4
              }}
            >
              <Edit3 size={15} /> Edit
            </button>
          ) : (
            <button
              onClick={() => {
                setFullName(user?.fullName || '');
                setMobile(user?.mobileNumber || '');
                if (profile) {
                  setFirmName(profile.firmName || '');
                  setGstin(profile.gstin || '');
                  setAddress1(profile.addressLine1 || '');
                  setAddress2(profile.addressLine2 || '');
                  setPincode(profile.pincode || '');
                  setState(profile.state || '');
                  setCity(profile.city || '');
                }
                setIsEditingProfile(false);
                setProfileMsg(null);
              }}
              style={{
                background: 'none', border: 'none', color: 'var(--muted)',
                cursor: 'pointer', fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 4
              }}
            >
              <X size={15} /> Cancel
            </button>
          )}
        </div>
        {profileMsg && <div style={S.msg(profileMsg.kind)}>{profileMsg.text}</div>}

        {/* Personal Details */}
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5 }}>Personal Information</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={S.label}>Full name</label>
            <input style={inputStyle} value={fullName} onChange={e => setFullName(e.target.value)} disabled={!isEditingProfile} placeholder="Full Name" />
          </div>
          <div>
            <label style={S.label}>Email Address (Read-only)</label>
            <input style={{ ...S.input, background: '#f5f5f5', cursor: 'not-allowed' }} value={user?.email || ''} disabled />
          </div>
          <div>
            <label style={S.label}>Mobile number</label>
            <input style={inputStyle} value={mobile} onChange={e => setMobile(e.target.value.replace(/[^0-9+\- ]/g, ''))} disabled={!isEditingProfile} placeholder="9876543210" />
          </div>
        </div>

        {/* Business Details */}
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 0.5, borderTop: '1px solid var(--border)', paddingTop: 14 }}>Business Profile</div>
        {kycLocked && (
          <div style={{ background: '#FFFBEB', color: '#B45309', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, marginBottom: 14 }}>
            🔒 Business profile and location preferences are locked because your KYC is {profile?.kyc?.status}. Contact support to request edits.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={S.label}>Firm / Shop Name</label>
            <input style={bizInputStyle} value={firmName} onChange={e => setFirmName(e.target.value)} disabled={!isEditingProfile || kycLocked} placeholder="Firm Name" />
          </div>
          <div>
            <label style={S.label}>GSTIN</label>
            <input style={bizInputStyle} value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())} disabled={!isEditingProfile || kycLocked} placeholder="GSTIN (Optional)" />
          </div>
        </div>

        {/* Location & Address */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={S.label}>Address Line 1</label>
            <input style={bizInputStyle} value={address1} onChange={e => setAddress1(e.target.value)} disabled={!isEditingProfile || kycLocked} placeholder="Street, Sector, Building, etc." />
          </div>
          <div>
            <label style={S.label}>Address Line 2</label>
            <input style={bizInputStyle} value={address2} onChange={e => setAddress2(e.target.value)} disabled={!isEditingProfile || kycLocked} placeholder="Area, Landmark, Suite, etc." />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={S.label}>State</label>
            {kycLocked ? (
              <input style={{ ...S.input, background: '#f5f5f5', cursor: 'not-allowed' }} value={state} disabled />
            ) : (
              <select style={selectStyle} value={state} disabled={!isEditingProfile} onChange={e => { setState(e.target.value); setCity(''); }}>
                <option value="">Select State</option>
                {allStates.map(st => <option key={st.state || st} value={st.state || st}>{st.state || st}</option>)}
              </select>
            )}
          </div>
          <div>
            <label style={S.label}>City</label>
            {kycLocked ? (
              <input style={{ ...S.input, background: '#f5f5f5', cursor: 'not-allowed' }} value={city} disabled />
            ) : (
              <select style={selectStyle} value={city} disabled={!isEditingProfile || !state} onChange={e => setCity(e.target.value)}>
                <option value="">{state ? 'Select City' : 'Pick state first'}</option>
                {allCities.map(ct => <option key={ct} value={ct}>{ct}</option>)}
              </select>
            )}
          </div>
          <div>
            <label style={S.label}>Pincode</label>
            <input style={bizInputStyle} value={pincode} onChange={e => setPincode(e.target.value.replace(/\D/g, ''))} disabled={!isEditingProfile || kycLocked} maxLength={6} placeholder="400001" />
          </div>
        </div>

        {isEditingProfile && (
          <button style={S.btn('primary', savingProfile)} onClick={saveProfile} disabled={savingProfile}>
            {savingProfile ? 'Saving…' : 'Save profile'}
          </button>
        )}
      </div>

      {/* Password */}
      <div style={{ ...S.card, cursor: !isEditingPwd ? 'pointer' : 'default' }} onClick={() => { if (!isEditingPwd) setIsEditingPwd(true); }}>
        <div style={{ ...S.sec, borderBottom: isEditingPwd ? '1px dashed var(--border)' : 'none', paddingBottom: isEditingPwd ? 8 : 0, marginBottom: isEditingPwd ? 12 : 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Change password</span>
          {!isEditingPwd ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--green)', fontSize: 13, fontWeight: 700 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>Click to expand</span>
              <ChevronDown size={18} />
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPwd({ currentPassword: '', newPassword: '', confirm: '' });
                setIsEditingPwd(false);
                setPwdMsg(null);
              }}
              style={{
                background: 'none', border: 'none', color: 'var(--muted)',
                cursor: 'pointer', fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 4
              }}
            >
              <X size={15} /> Cancel
            </button>
          )}
        </div>
        {isEditingPwd && (
          <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12 }}>
            {pwdMsg && <div style={S.msg(pwdMsg.kind)}>{pwdMsg.text}</div>}
            <div style={S.row}>
              <div>
                <label style={S.label}>Current password</label>
                <input type="password" style={pwdInputStyle} value={pwd.currentPassword} disabled={!isEditingPwd} onChange={e => setPwd(p => ({ ...p, currentPassword: e.target.value }))} />
              </div>
              <div />
              <div>
                <label style={S.label}>New password</label>
                <input type="password" style={pwdInputStyle} value={pwd.newPassword} disabled={!isEditingPwd} onChange={e => setPwd(p => ({ ...p, newPassword: e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Confirm new password</label>
                <input type="password" style={pwdInputStyle} value={pwd.confirm} disabled={!isEditingPwd} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} />
              </div>
            </div>
            <button
              style={S.btn('primary', savingPwd)}
              onClick={changePassword}
              disabled={savingPwd || !pwd.currentPassword || !pwd.newPassword || !pwd.confirm}
            >
              {savingPwd ? 'Updating…' : 'Update password'}
            </button>
          </div>
        )}
      </div>

      {/* Account Deletion */}
      <div style={{ ...S.card, border: '1px solid #FEE2E2', background: '#FEF2F2', padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#991B1B', marginBottom: 8 }}>Account Deletion</div>
        <p style={{ fontSize: 13, color: '#7F1D1D', marginBottom: 16, lineHeight: 1.5 }}>
          Once you delete your account, there is no going back. Please be certain. This will suspend your vendor profile and disable your vendor status.
        </p>
        <button
          onClick={handleDeleteAccount}
          style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          Delete Account
        </button>
      </div>

      {/* Modern Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '12px',
            width: '90%',
            maxWidth: '370px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            {!deleteSuccess ? (
              <>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#FEE2E2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#DC2626'
                }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#1A1A1A',
                  marginBottom: '12px'
                }}>
                  Delete Account?
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--muted)',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Are you sure you want to permanently delete your account? This action cannot be undone and will suspend your vendor profile.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      borderRadius: '9px',
                      border: '1.5px solid var(--border)',
                      background: '#fff',
                      color: 'var(--muted)',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteAccount}
                    disabled={deleting}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      borderRadius: '9px',
                      border: 'none',
                      background: '#DC2626',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      opacity: deleting ? 0.6 : 1,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#B91C1C')}
                    onMouseLeave={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#DC2626')}
                  >
                    {deleting ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--green-lt)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: 'var(--green)'
                }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#1A1A1A',
                  marginBottom: '12px'
                }}>
                  Account Deleted
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--muted)',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Your account has been deleted successfully.
                </p>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    logout();
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    borderRadius: '9px',
                    border: 'none',
                    background: 'var(--green)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--green-dk)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--green)'}
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
